// @flow
/* eslint-disable ta-code-conventions/has-matching-test */

import * as React from 'react';

import { NavigableList } from '@ta/list.navigable-list';
import TypeaheadJsonResult from '../results/TypeaheadJsonResult';
import type { TypeaheadResult } from '../TypeaheadResult';

import Selector from './Selector';
import TypeaheadJsonFetcher from '../fetchers/TypeaheadJsonFetcher';
import WorldwideFetcher from '../fetchers/WorldwideFetcher';
import type { IconStyle } from '../results/BasicResult';

type Props = {|
  /** The currently selected result. */
  selectedLocation: TypeaheadResult,
  /** Callback triggered when the selection changes. */
  selectionChanged: TypeaheadResult => void,
  /** Callback triggered when the selection is manually cleared */
  selectionCleared?: () => void,
  /** Callback triggered when user changes text input */
  textInputChanged?: string => void,
  /** Mode to run in. Defaults to 'all' */
  mode: 'all' | 'cities',
  /** Whether or not 'Worldwide' should be included in the results. Defaults to true. */
  includeWorld?: boolean,
  /** Optionally customize icon type for the beginning of input box, default is 'search' */
  iconType?: 'search' | 'map-pin-fill',
  /** Optionally specify the icon color (green, or default gray) in the selector. */
  selectorGreenIcon?: boolean,
  /** Optionally specify the icon color (green, or default gray) in the result items. */
  resultIconStyle?: IconStyle,
  /** Optionally customize the placeholder text */
  placeholder?: string,
  /** A CSS class name to apply to the <input/> */
  className?: string,
  /** Optionally customize the max number of list result, default to be 8 */
  max?: number,
  /** Callback for when the typeahead input loses focus. */
  onBlur?: (e: SyntheticEvent<HTMLInputElement>) => void,
  /** Callback for when the typeahead input set focus. */
  onFocus?: (e: SyntheticEvent<HTMLInputElement>) => void,
  /** Default to be false. If true, dropdown will be closed when click outside the typeahead */
  closeDropdownOnClickOutside?: boolean,
  /** Position the typeahead results above the input field */
  resultsAbove?: boolean,
|};

type State = {|
  textInputValue: string,
|};

/**
 * Component that lets you pick a location, specifically Geos, with typeahead support.
 */
class GeoSelector extends React.Component<Props, State> {
  static defaultProps = {
    mode: 'all',
    includeWorld: true,
    selectionCleared: () => {},
    textInputChanged: () => {},
    iconType: 'search',
    selectorGreenIcon: false,
    resultIconStyle: undefined,
    placeholder: undefined,
    max: 8,
    onBlur: undefined,
    onFocus: undefined,
    closeDropdownOnClickOutside: true,
    resultsAbove: false,
  };

  state = {
    textInputValue: this.props.selectedLocation.displayValue || '',
  };

  onResultSelected = (selectedResult: TypeaheadResult) => {
    this.props.selectionChanged(selectedResult);
    this.setState({ textInputValue: selectedResult.displayValue || '' });
  };

  typeaheadJsonFetcher = new TypeaheadJsonFetcher({
    interleaved: true,
    types: ['geo'],
    filter: this.props.mode === 'cities' ? ['nobroad'] : undefined,
    name_depth: 1,
    details: true,
    legacy_format: true,
    urlList: false,
    max: this.props.max,
  });

  textInputChanged = (newValue: string) => {
    if (this.props.textInputChanged) {
      this.props.textInputChanged(newValue);
    }

    if (newValue === '' && this.props.selectionCleared) {
      this.props.selectionCleared();
    }

    this.setState({ textInputValue: newValue });
  };

  render() {
    const fetchers = [this.typeaheadJsonFetcher];
    if (this.props.includeWorld) {
      fetchers.unshift(new WorldwideFetcher());
    }
    return (
      <Selector
        fetchers={fetchers}
        className={this.props.className}
        typeaheadTextInputValue={this.state.textInputValue}
        selectionChanged={this.onResultSelected}
        textInputChanged={this.textInputChanged}
        iconType={this.props.iconType}
        greenIcon={this.props.selectorGreenIcon}
        placeHolder={this.props.placeholder}
        onFocus={this.props.onFocus}
        onBlur={this.props.onBlur}
        closeDropdownOnClickOutside={this.props.closeDropdownOnClickOutside}
        resultsAbove={this.props.resultsAbove}
      >
        {(results, tracker, onSelection) => (
          <NavigableList>
            {[...(results.worldwide || []), ...(results.typeaheadjson || [])].map(
              r => (isActive, onMouseMove) => (
                <TypeaheadJsonResult
                  result={r}
                  tracker={tracker}
                  onMouseMove={onMouseMove}
                  onSelection={onSelection}
                  active={isActive}
                  key={`result=${r.value}`}
                  iconStyle={this.props.resultIconStyle}
                  includeIcon
                />
              ),
            )}
          </NavigableList>
        )}
      </Selector>
    );
  }
}

export default GeoSelector;
